import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, CheckCircle2, Loader2, Lock, Save } from 'lucide-react';
import { useStore } from '../../../context/StoreContext';
import { useAuth } from '../../../context/AuthContext';
import { SMIIFY_API } from '../../../config/api';
import AccessEditor from '../../../components/AccessMatrix/AccessEditor';
import styles from './ManageAccess.module.css';

const API = `${SMIIFY_API}/access`;
const ROLE_LABEL = { ADMIN: 'Admin', MANAGER: 'Manager', RECEPTIONIST: 'Receptionist', STAFF: 'Staff', SUPERADMIN: 'HQ owner' };
const storeIdOf = (s) => String(s?._id || s?.id || '');

const countDiff = (a = {}, b = {}) => {
    let n = 0;
    Object.keys(b).forEach((m) => {
        Object.keys(b[m]?.actions || {}).forEach((k) => { if (Boolean(a?.[m]?.actions?.[k]) !== Boolean(b[m].actions[k])) n += 1; });
        Object.keys(b[m]?.constraints || {}).forEach((k) => { if (String(a?.[m]?.constraints?.[k]) !== String(b[m].constraints[k])) n += 1; });
    });
    return n;
};
const fmt = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

export default function ManageAccess() {
    const { userId } = useParams();
    const navigate = useNavigate();
    const { availableStores = [], activeStore } = useStore();
    const { refreshAccess, user: me } = useAuth();
    const token = localStorage.getItem('smiify_token');
    const storeId = storeIdOf(activeStore);

    const [data, setData] = useState(null);
    const [draft, setDraft] = useState({});
    const [role, setRole] = useState('');
    const [stores, setStores] = useState([]);
    const [preset, setPreset] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);
    const [blocked, setBlocked] = useState([]);

    const headers = useMemo(() => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(storeId && storeId !== 'global_hq' ? { 'x-store-id': storeId } : {}) }), [token, storeId]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API}/users/${encodeURIComponent(userId)}`, { headers });
            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json.success) throw new Error(json.message || 'Could not load access.');
            setData(json.data);
            setDraft(json.data.policies);
            setRole(json.data.user.role);
            setStores(json.data.assignedStores || []);
        } catch (e) {
            setMsg({ type: 'error', text: e.message === 'Failed to fetch' ? 'Cannot reach the server.' : e.message });
        } finally {
            setLoading(false);
        }
    }, [userId, headers]);

    useEffect(() => { load(); }, [load]);

    const editor = data?.editor || {};

    // Switches that are on for this user but turned off by the HQ limit of the branch
    const blockedByHq = useMemo(() => {
        if (!data?.ceiling) return [];
        const out = [];
        (data.blueprint || []).forEach((m) => {
            if (m.hqOnly) return;
            Object.entries(m.actions).forEach(([k, d]) => {
                if (draft?.[m.id]?.actions?.[k] === true && data.ceiling?.[m.id]?.actions?.[k] === false) out.push(`${m.name}: ${d.label}`);
            });
        });
        return out;
    }, [data, draft]);
    const readOnly = !editor.canManage;
    const changes = data ? countDiff(data.policies, draft) : 0;
    const roleChanged = data && role !== data.user.role;
    const storesChanged = data && (stores.length !== data.assignedStores.length || stores.some((s) => !data.assignedStores.includes(s)));
    const dirty = changes > 0 || roleChanged || storesChanged;
    const branchList = availableStores.filter((s) => !s.isHQ && storeIdOf(s) !== 'global_hq');
    const canGiveStore = (id) => editor.assignableStores === null || editor.assignableStores === undefined || editor.assignableStores.includes(id);

    const save = async (body, okText) => {
        setSaving(true);
        setMsg(null);
        setBlocked([]);
        try {
            const res = await fetch(`${API}/users/${encodeURIComponent(userId)}`, { method: 'PUT', headers, body: JSON.stringify(body) });
            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json.success) throw new Error(json.message || 'Could not save.');
            setBlocked(json.data?.blocked || []);
            setMsg({ type: json.data?.blocked?.length ? 'warn' : 'ok', text: json.message || okText });
            await load();
            refreshAccess?.();
        } catch (e) {
            setMsg({ type: 'error', text: e.message === 'Failed to fetch' ? 'Cannot reach the server.' : e.message });
        } finally {
            setSaving(false);
        }
    };

    const onSave = () => save({
        policies: draft,
        ...(roleChanged ? { role } : {}),
        ...(storesChanged ? { assignedStores: stores } : {}),
    }, 'Saved.');

    const onPreset = () => {
        if (!preset) return;
        if (!window.confirm(`Replace ${data.user.name}'s access with the default for ${ROLE_LABEL[preset]}? Anything above your access or the HQ limit is skipped.`)) return;
        save({ preset, ...(roleChanged ? { role } : {}), ...(storesChanged ? { assignedStores: stores } : {}) }, 'Preset applied.');
        setPreset('');
    };

    if (loading && !data) return <div className={styles.center}><Loader2 size={20} className={styles.spin} /> Loading access...</div>;
    if (!data) {
        return (
            <div className={styles.center}>
                <AlertTriangle size={20} /> {msg?.text || 'Not found.'}
                <button type="button" className={styles.btn} onClick={() => navigate(-1)}>Go back</button>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <div className={styles.headLeft}>
                    <button type="button" className={styles.iconBtn} onClick={() => navigate(-1)} aria-label="Back"><ArrowLeft size={15} /></button>
                    <div>
                        <h1>Access for {data.user.name}</h1>
                        <p>
                            <span className={styles.roleChip}>{ROLE_LABEL[data.user.role] || data.user.role}</span>
                            {data.user.username && <span> @{data.user.username}</span>}
                            {data.ceiling && <span className={styles.hqNote}><Lock size={11} /> HQ branch limit applies</span>}
                        </p>
                    </div>
                </div>
                <div className={styles.headRight}>
                    {dirty && <span className={styles.dirty}>{changes + (roleChanged ? 1 : 0) + (storesChanged ? 1 : 0)} unsaved change(s)</span>}
                    {dirty && <button type="button" className={styles.btn} onClick={() => { setDraft(data.policies); setRole(data.user.role); setStores(data.assignedStores); }}>Discard</button>}
                    <button type="button" className={styles.btnPrimary} onClick={onSave} disabled={readOnly || !dirty || saving}>
                        {saving ? <Loader2 size={13} className={styles.spin} /> : <Save size={13} />} Save
                    </button>
                </div>
            </header>

            <div className={styles.body}>
                {readOnly && <div className={styles.banner}><Lock size={13} /> {editor.reason || 'You can only view this access.'}</div>}
                {blockedByHq.length > 0 && (
                    <div className={`${styles.msg} ${styles.msg_warn}`} role="status">
                        <Lock size={14} />
                        <div>
                            <b>{blockedByHq.length} switch(es) are on here but turned off by the HQ limit for this branch.</b>
                            <ul>{blockedByHq.slice(0, 6).map((b) => <li key={b}>{b}</li>)}{blockedByHq.length > 6 && <li>and {blockedByHq.length - 6} more</li>}</ul>
                            {me?.role === 'SUPERADMIN'
                                ? <button type="button" className={styles.btn} style={{ marginTop: 6 }} onClick={() => navigate('/global-access')}>Open branch limits</button>
                                : <small>Ask HQ to allow them in Branch access limits.</small>}
                        </div>
                    </div>
                )}
                {msg && (
                    <div className={`${styles.msg} ${styles[`msg_${msg.type}`]}`} role="status">
                        {msg.type === 'ok' ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
                        <div>
                            <b>{msg.text}</b>
                            {blocked.length > 0 && <ul>{blocked.slice(0, 8).map((b) => <li key={b}>{b}</li>)}{blocked.length > 8 && <li>and {blocked.length - 8} more</li>}</ul>}
                        </div>
                    </div>
                )}

                <div className={styles.cards}>
                    <section className={styles.card}>
                        <h3>Role</h3>
                        {editor.canChangeRole ? (
                            <select className={styles.select} value={role} onChange={(e) => setRole(e.target.value)} aria-label="Role">
                                {[...new Set([data.user.role, ...(editor.assignableRoles || [])])].map((r) => <option key={r} value={r} disabled={r !== data.user.role && !(editor.assignableRoles || []).includes(r)}>{ROLE_LABEL[r] || r}</option>)}
                            </select>
                        ) : <p className={styles.value}>{ROLE_LABEL[data.user.role] || data.user.role}</p>}
                        <small>You can give only roles below your own.</small>
                    </section>

                    <section className={styles.card}>
                        <h3>Branches</h3>
                        <div className={styles.storeList}>
                            {branchList.map((s) => {
                                const id = storeIdOf(s);
                                const on = stores.includes(id);
                                const allowed = editor.canAssignBranches && canGiveStore(id);
                                return (
                                    <label key={id} className={`${styles.storeRow} ${!allowed ? styles.storeOff : ''}`}>
                                        <input type="checkbox" checked={on} disabled={!allowed || readOnly} onChange={(e) => setStores(e.target.checked ? [...stores, id] : stores.filter((x) => x !== id))} />
                                        <span>{s.storeName || s.name}</span>
                                        {!allowed && on && <Lock size={10} />}
                                    </label>
                                );
                            })}
                            {!branchList.length && <small>No branches.</small>}
                        </div>
                        <small>{editor.canAssignBranches ? 'Only branches you have yourself can be given.' : 'You cannot change branches.'}</small>
                    </section>

                    <section className={styles.card}>
                        <h3>Start from a preset</h3>
                        <div className={styles.presetRow}>
                            <select className={styles.select} value={preset} onChange={(e) => setPreset(e.target.value)} disabled={readOnly} aria-label="Preset">
                                <option value="">Choose role defaults</option>
                                {(editor.assignableRoles || []).map((r) => <option key={r} value={r}>{ROLE_LABEL[r]} defaults</option>)}
                            </select>
                            <button type="button" className={styles.btn} onClick={onPreset} disabled={!preset || readOnly || saving}>Apply</button>
                        </div>
                        <small>Replaces every switch with that role's defaults, within your limits.</small>
                    </section>
                </div>

                <AccessEditor
                    blueprint={data.blueprint}
                    categories={data.categories}
                    value={draft}
                    onChange={setDraft}
                    limit={data.grantable}
                    ceiling={data.ceiling}
                    readOnly={readOnly}
                />

                {data.history?.length > 0 && (
                    <section className={styles.history}>
                        <h3>Recent changes</h3>
                        {data.history.map((h) => (
                            <div key={h._id} className={styles.histRow}>
                                <div><b>{h.actorName || 'Someone'}</b> <span>({h.actorRole})</span> <em>{fmt(h.createdAt)}</em></div>
                                <ul>{(h.changes || []).slice(0, 6).map((c) => <li key={c}>{c}</li>)}{h.changes?.length > 6 && <li>+{h.changes.length - 6} more</li>}</ul>
                                {h.blocked?.length > 0 && <small>{h.blocked.length} item(s) were above the limit and not given.</small>}
                            </div>
                        ))}
                    </section>
                )}
            </div>
        </div>
    );
}